/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useCallback, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { useAtlassianUserContext } from '../../../../hooks/use-user-context';
import { CollectFeedbackV2 } from '../../CollectFeedback';
import { UNAUTHENTICATED_FEEDBACK_FORM_URL } from '../constants';
import { messages } from '../messages';

const styles = cssMap({
	feedbackButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		border: 'none',
		borderRadius: token('radius.medium'),
		backgroundColor: 'transparent',
		color: 'inherit',
		font: 'inherit',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'@media (max-width: 30rem)': {
			display: 'none',
		},
	},
});

type FeedbackControlProps = {
	testId?: string;
};

export const FeedbackControl = ({
	testId = 'feedback-control',
}: FeedbackControlProps): JSX.Element => {
	const { identity: user, isAuthed } = useAtlassianUserContext();
	const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

	const handleFeedbackOpen = useCallback((): void => {
		if (!isAuthed) {
			globalThis.window.open(UNAUTHENTICATED_FEEDBACK_FORM_URL, '_blank');
			return;
		}

		setIsFeedbackOpen(true);
	}, [isAuthed]);

	const handleFeedbackClose = useCallback((): void => {
		setIsFeedbackOpen(false);
	}, []);

	return (
		<Fragment>
			<button
				type="button"
				css={styles.feedbackButton}
				onClick={handleFeedbackOpen}
				data-testid={testId}
			>
				<FormattedMessage {...messages.sendFeedback} />
			</button>
			<CollectFeedbackV2
				atlassianAccountId={user.accountId}
				name={user.name}
				isOpen={isFeedbackOpen}
				onClose={handleFeedbackClose}
			/>
		</Fragment>
	);
};
